<template>
  <div class="block-editor">
    <!-- Empty state -->
    <div v-if="store.richContent.length === 0" class="editor-empty">
      <q-icon name="view_agenda" size="40px" style="color: var(--oa-text-muted); opacity: 0.4;" />
      <p class="empty-heading">Sin contenido todavía</p>
      <p class="empty-body">Agregá un bloque para comenzar a armar la lección.</p>
    </div>

    <!-- Block list -->
    <div v-else class="block-list">
      <BlockItem
        v-for="(block, idx) in store.richContent"
        :key="blockIds[idx]"
        :block="block"
        :is-first="idx === 0"
        :is-last="idx === store.richContent.length - 1"
        :is-dragging="draggingIndex === idx"
        @update="store.updateBlock(idx, $event)"
        @remove="handleRemove(idx)"
        @move-up="handleMove(idx, idx - 1)"
        @move-down="handleMove(idx, idx + 1)"
        @drag-start="onDragStart(idx)"
        @drag-over="onDragOver(idx)"
        @drop="onDrop"
        @drag-end="onDragEnd"
      />
    </div>

    <!-- Add block button -->
    <div class="add-block-row">
      <q-btn
        outline
        no-caps
        icon="add"
        label="Agregar bloque"
        color="primary"
        class="add-block-btn"
        @click="showPalette = true"
      />
    </div>

    <!-- Block palette — bottom drawer -->
    <q-dialog v-model="showPalette" position="bottom">
      <q-card style="width: 100%; max-width: 680px; max-height: 80vh; overflow-y: auto;">
        <q-card-section class="palette-header">
          <div class="palette-title">Agregar bloque</div>
          <q-btn flat round dense icon="close" @click="showPalette = false" />
        </q-card-section>
        <q-separator />
        <BlockPalette @add="handleAdd" />
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useAdminCoursesStore } from 'src/stores/useAdminCoursesStore';
import type { ContentBlock } from 'src/stores/useAdminCoursesStore';
import BlockItem from './BlockItem.vue';
import BlockPalette from './BlockPalette.vue';

const store = useAdminCoursesStore();
const showPalette = ref(false);

// Stable per-block IDs for keying (transient — reset when blocks change count)
const blockIds = ref<string[]>([]);

function ensureIds() {
  while (blockIds.value.length < store.richContent.length) {
    blockIds.value.push(crypto.randomUUID());
  }
  if (blockIds.value.length > store.richContent.length) {
    blockIds.value = blockIds.value.slice(0, store.richContent.length);
  }
}

// Keep IDs in sync whenever richContent length changes
watch(
  () => store.richContent.length,
  () => ensureIds(),
  { immediate: true },
);

function handleAdd(block: ContentBlock) {
  blockIds.value.push(crypto.randomUUID());
  store.addBlock(block);
  showPalette.value = false;
}

function handleRemove(idx: number) {
  blockIds.value.splice(idx, 1);
  store.removeBlock(idx);
}

function handleMove(from: number, to: number) {
  if (to < 0 || to >= store.richContent.length) return;
  const [id] = blockIds.value.splice(from, 1);
  blockIds.value.splice(to, 0, id);
  store.moveBlock(from, to);
}

// Drag-and-drop
const draggingIndex = ref<number | null>(null);
const overIndex = ref<number | null>(null);

function onDragStart(idx: number) {
  draggingIndex.value = idx;
}

function onDragOver(idx: number) {
  overIndex.value = idx;
}

function onDrop() {
  if (draggingIndex.value === null || overIndex.value === null) return;
  if (draggingIndex.value === overIndex.value) {
    draggingIndex.value = null;
    overIndex.value = null;
    return;
  }
  handleMove(draggingIndex.value, overIndex.value);
  draggingIndex.value = null;
  overIndex.value = null;
}

function onDragEnd() {
  draggingIndex.value = null;
  overIndex.value = null;
}
</script>

<style scoped lang="scss">
.block-editor {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.editor-empty {
  padding: 48px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.empty-heading {
  font-size: 15px;
  font-weight: 600;
  color: var(--oa-text-secondary);
  margin: 0;
}

.empty-body {
  font-size: 13px;
  color: var(--oa-text-muted);
  margin: 0;
}

.block-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
}

.add-block-row {
  padding: 12px 16px;
  border-top: 1px solid var(--oa-border);
}

.add-block-btn {
  width: 100%;
}

.palette-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
}

.palette-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--oa-text-primary);
}
</style>
